import { useEffect, useState } from "react";
import {
  EuiBadge,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLink,
  EuiMarkdownFormat,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { GitLabUser } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { GitLabUserAvatar } from "../gitlab";
import { WaveSummary, formatTimeAgo } from "./model";

/**
 * The wave's current update merge request: state, who is on it, and the change
 * summary behind a toggle. Collapsed by default so the Markdown body does not
 * push the component table below the fold.
 */
export const WaveMergePanel = ({
  summary,
  initiallyOpen = false,
}: {
  summary: WaveSummary;
  initiallyOpen?: boolean;
}): JSX.Element | null => {
  const { translate } = useAnketaI18n();
  const [isOpen, setIsOpen] = useState<boolean>(initiallyOpen);
  const merge = summary.merge;

  // A different wave means a different merge request; start collapsed again.
  useEffect(() => setIsOpen(initiallyOpen), [summary.wave, initiallyOpen]);

  if (!merge) return null;

  const merged = merge.state === "merged";
  const people: Array<{ user: GitLabUser; role: string }> = [];
  if (merge.author)
    people.push({
      user: merge.author,
      role: translate("anketa.opsanio.waves.role.author", {
        defaultMessage: "Author",
      }),
    });
  if (merge.merged_by)
    people.push({
      user: merge.merged_by,
      role: translate("anketa.opsanio.waves.role.merged_by", {
        defaultMessage: "Merged by",
      }),
    });
  (merge.reviewers ?? []).forEach((user) =>
    people.push({
      user,
      role: translate("anketa.medocino.support.reviewer.label", {
        defaultMessage: "Reviewer",
      }),
    }),
  );
  (merge.assignees ?? []).forEach((user) =>
    people.push({
      user,
      role: translate("anketa.opsanio.waves.role.assignee", {
        defaultMessage: "Assignee",
      }),
    }),
  );

  const meta = merged
    ? translate("anketa.opsanio.waves.merged_ago", {
        values: { time: formatTimeAgo(merge.merged_at, translate) },
        defaultMessage: "merged {{time}}",
      })
    : translate("anketa.opsanio.waves.opened_ago", {
        values: { time: formatTimeAgo(merge.created_at, translate) },
        defaultMessage: "opened {{time}}",
      });

  return (
    <EuiPanel hasBorder paddingSize="m" hasShadow={false} color="subdued">
      <EuiFlexGroup gutterSize="m" alignItems="flexStart" wrap>
        <EuiFlexItem style={{ minWidth: 0 }}>
          <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiBadge color={merged ? "success" : "primary"}>
                {merge.state}
              </EuiBadge>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiLink href={merge.web_url} target="_blank">
                <strong>{merge.title}</strong>
              </EuiLink>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                !{merge.iid}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size="xs" />
          <EuiText size="xs" color="subdued">
            {meta} · {merge.source_branch} → {merge.target_branch}
          </EuiText>
        </EuiFlexItem>

        {people.length > 0 && (
          <EuiFlexItem grow={false}>
            <EuiFlexGroup gutterSize="m" responsive={false} wrap>
              {people.map(({ user, role }, index) => (
                <EuiFlexItem grow={false} key={`${user.id}-${role}-${index}`}>
                  <GitLabUserAvatar user={user} />
                  <EuiText size="xs" color="subdued" textAlign="center">
                    {role}
                  </EuiText>
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      {merge.description && (
        <>
          <EuiSpacer size="s" />
          <EuiButtonEmpty
            size="xs"
            flush="left"
            iconSide="right"
            iconType={isOpen ? "arrowUp" : "arrowDown"}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen
              ? translate("anketa.opsanio.waves.merge_description.hide", {
                  defaultMessage: "Hide change summary",
                })
              : translate("anketa.opsanio.waves.merge_description.show", {
                  defaultMessage: "Show change summary",
                })}
          </EuiButtonEmpty>
          {isOpen && (
            <>
              <EuiSpacer size="s" />
              <EuiPanel color="plain" paddingSize="m" hasShadow={false} hasBorder>
                <EuiMarkdownFormat>{merge.description}</EuiMarkdownFormat>
              </EuiPanel>
            </>
          )}
        </>
      )}
    </EuiPanel>
  );
};
